import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, Upload, X, FileIcon, Loader2, AlertCircle } from 'lucide-react';
// ⚠️ Replace these with your actual EmailJS credentials
// 1. Sign up at https://www.emailjs.com/
// 2. Create an Email Service (e.g. Gmail) → copy the Service ID
// 3. Create an Email Template → copy the Template ID
// 4. Go to Account → copy your Public Key
const EMAILJS_SERVICE_ID = 'service_a59f64j';
const EMAILJS_TEMPLATE_ID = 'template_4kcaq9i';
const EMAILJS_PUBLIC_KEY = 'QMYm1a4R_ZhZ_OGjg';
export function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    location: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || 'Not provided',
      company: formData.company || 'Not provided',
      service: formData.service || 'Not specified',
      location: formData.location || 'Not provided',
      message: formData.message,
      files_info: files.length > 0 ? `Attached files (sent separately): ${files.map(f => f.name).join(', ')}` : 'No files attached'
    };
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      setError('Failed to send message. Please try again or email us directly at Terran@sandevindustries.com');
    } finally {
      setSending(false);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  return <div className="min-h-screen bg-sandev-bg" data-id="element-140">
      {/* Header */}
      <section className="pt-24 pb-16 bg-sandev-card border-b border-sandev-border" data-id="element-141">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-142">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-3xl" data-id="element-143">
            <h1 className="text-4xl md:text-5xl font-bold text-sandev-text mb-6" data-id="element-144">
              Get In Touch
            </h1>
            <p className="text-xl text-sandev-muted leading-relaxed" data-id="element-145">
              Have a project in mind? Whether you need a quick prototype or a
              full production run, we're here to help. Reach out and we'll get
              back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20" data-id="element-146">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-147">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12" data-id="element-148">
            {/* Contact Info Sidebar */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="lg:col-span-2 space-y-8" data-id="element-149">
              <div data-id="element-150">
                <h2 className="text-2xl font-bold text-sandev-text mb-6" data-id="element-151">
                  Contact Information
                </h2>
                <p className="text-sandev-muted leading-relaxed mb-8" data-id="element-152">
                  Reach out directly or fill out the form and our team will
                  respond promptly with a free consultation and quote.
                </p>
              </div>

              <div className="space-y-6" data-id="element-153">
                <div className="flex items-start gap-4" data-id="element-154">
                  <div className="w-12 h-12 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center shrink-0" data-id="element-155">
                    <Mail className="text-sandev-accent" size={20} data-id="element-156" />
                  </div>
                  <div data-id="element-157">
                    <h4 className="text-sm font-semibold text-sandev-text mb-1" data-id="element-158">
                      Email
                    </h4>
                    <p className="text-sandev-muted text-sm" data-id="element-159">
                      Terran@sandevindustries.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-160">
                  <div className="w-12 h-12 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center shrink-0" data-id="element-161">
                    <Phone className="text-sandev-accent" size={20} data-id="element-162" />
                  </div>
                  <div data-id="element-163">
                    <h4 className="text-sm font-semibold text-sandev-text mb-1" data-id="element-164">
                      Phone
                    </h4>
                    <p className="text-sandev-muted text-sm" data-id="element-165">(Coming Soon)</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-166">
                  <div className="w-12 h-12 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center shrink-0" data-id="element-167">
                    <MapPin className="text-sandev-accent" size={20} data-id="element-168" />
                  </div>
                  <div data-id="element-169">
                    <h4 className="text-sm font-semibold text-sandev-text mb-1" data-id="element-170">
                      Location
                    </h4>
                    <p className="text-sandev-muted text-sm" data-id="element-171">
                      Kitchener Ontario, Canada
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-172">
                  <div className="w-12 h-12 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center shrink-0" data-id="element-173">
                    <Clock className="text-sandev-accent" size={20} data-id="element-174" />
                  </div>
                  <div data-id="element-175">
                    <h4 className="text-sm font-semibold text-sandev-text mb-1" data-id="element-176">
                      Business Hours
                    </h4>
                    <p className="text-sandev-muted text-sm" data-id="element-177">
                      Mon – Fri: 8:00 AM – 6:00 PM
                      <br data-id="element-178" />
                      Sat: 9:00 AM – 1:00 PM
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick info card */}
              <div className="bg-sandev-card border border-sandev-border rounded-xl p-6 mt-8" data-id="element-179">
                <h4 className="text-sm font-bold text-sandev-accent uppercase tracking-wider mb-3" data-id="element-180">
                  What to expect
                </h4>
                <ul className="space-y-3 text-sm text-sandev-muted" data-id="element-181">
                  <li className="flex items-start gap-2" data-id="element-182">
                    <CheckCircle size={16} className="text-sandev-accent mt-0.5 shrink-0" data-id="element-183" />
                    Free initial consultation (video call or on-site)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-184">
                    <CheckCircle size={16} className="text-sandev-accent mt-0.5 shrink-0" data-id="element-185" />
                    Quote within 24 hours
                  </li>
                  <li className="flex items-start gap-2" data-id="element-186">
                    <CheckCircle size={16} className="text-sandev-accent mt-0.5 shrink-0" data-id="element-187" />
                    Material & process recommendation
                  </li>
                  <li className="flex items-start gap-2" data-id="element-188">
                    <CheckCircle size={16} className="text-sandev-accent mt-0.5 shrink-0" data-id="element-189" />
                    Manufacturability analysis included
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="lg:col-span-3" data-id="element-190">
              <div className="bg-sandev-card border border-sandev-border rounded-2xl p-8 md:p-10" data-id="element-191">
                {submitted ? <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="text-center py-16" data-id="element-192">
                    <div className="w-20 h-20 rounded-full bg-sandev-accent/10 border border-sandev-accent/30 flex items-center justify-center mx-auto mb-6" data-id="element-193">
                      <CheckCircle className="text-sandev-accent" size={40} data-id="element-194" />
                    </div>
                    <h3 className="text-2xl font-bold text-sandev-text mb-3" data-id="element-195">
                      Message Sent!
                    </h3>
                    <p className="text-sandev-muted max-w-md mx-auto mb-8" data-id="element-196">
                      Thank you for reaching out. Our team will review your
                      inquiry and get back to you within 24 hours with a
                      consultation and quote.
                    </p>
                    <button onClick={() => {
                  setSubmitted(false);
                  setError(null);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    company: '',
                    service: '',
                    location: '',
                    message: ''
                  });
                  setFiles([]);
                }} className="text-sandev-accent hover:text-sandev-accentHover font-semibold transition-colors" data-id="element-197">
                      Send another message
                    </button>
                  </motion.div> : <>
                    <h3 className="text-xl font-bold text-sandev-text mb-2" data-id="element-198">
                      Request a Quote
                    </h3>
                    <p className="text-sandev-muted text-sm mb-8" data-id="element-199">
                      Fill out the form below and we'll get back to you
                      promptly.
                    </p>

                    {error && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3" data-id="element-200">
                        <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" data-id="element-201" />
                        <p className="text-sm text-red-400" data-id="element-202">{error}</p>
                      </motion.div>}

                    <form onSubmit={handleSubmit} className="space-y-6" data-id="element-203">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-204">
                        <div data-id="element-205">
                          <label htmlFor="name" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-206">
                            Full Name{' '}
                            <span className="text-sandev-accent" data-id="element-207">*</span>
                          </label>
                          <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} placeholder="John Doe" className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text placeholder-sandev-muted/50 focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm" data-id="element-208" />
                        </div>
                        <div data-id="element-209">
                          <label htmlFor="email" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-210">
                            Email Address{' '}
                            <span className="text-sandev-accent" data-id="element-211">*</span>
                          </label>
                          <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@company.com" className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text placeholder-sandev-muted/50 focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm" data-id="element-212" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-213">
                        <div data-id="element-214">
                          <label htmlFor="phone" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-215">
                            Phone Number
                          </label>
                          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text placeholder-sandev-muted/50 focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm" data-id="element-216" />
                        </div>
                        <div data-id="element-217">
                          <label htmlFor="company" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-218">
                            Company Name
                          </label>
                          <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Acme Manufacturing" className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text placeholder-sandev-muted/50 focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm" data-id="element-219" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-220">
                        <div data-id="element-221">
                          <label htmlFor="service" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-222">
                            Service Needed
                          </label>
                          <select id="service" name="service" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm appearance-none" data-id="element-223">
                            <option value="" className="text-sandev-muted" data-id="element-224">
                              Select a service
                            </option>
                            <option value="cad-design" data-id="element-225">CAD Design</option>
                            <option value="fdm-printing" data-id="element-226">FDM Printing</option>
                            <option value="resin-printing" data-id="element-227">
                              Resin Printing
                            </option>
                            <option value="full-prototype" data-id="element-228">
                              Full Prototype (Design + Print)
                            </option>
                            <option value="consultation" data-id="element-229">
                              General Consultation
                            </option>
                          </select>
                        </div>
                        <div data-id="element-230">
                          <label htmlFor="location" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-231">
                            Location
                          </label>
                          <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} placeholder="City, State / Country" className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text placeholder-sandev-muted/50 focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm" data-id="element-232" />
                        </div>
                      </div>

                      <div data-id="element-233">
                        <label htmlFor="message" className="block text-sm font-medium text-sandev-text mb-2" data-id="element-234">
                          Project Details{' '}
                          <span className="text-sandev-accent" data-id="element-235">*</span>
                        </label>
                        <textarea id="message" name="message" required rows={6} value={formData.message} onChange={handleChange} placeholder="Describe your project, part requirements, quantities, materials of interest, timeline, etc." className="w-full px-4 py-3 bg-sandev-bg border border-sandev-border rounded-lg text-sandev-text placeholder-sandev-muted/50 focus:outline-none focus:border-sandev-accent focus:ring-1 focus:ring-sandev-accent transition-colors text-sm resize-none" data-id="element-236" />
                      </div>

                      {/* File Upload */}
                      <div data-id="element-237">
                        <label className="block text-sm font-medium text-sandev-text mb-2" data-id="element-238">
                          Upload Files
                        </label>
                        <p className="text-xs text-sandev-muted mb-3" data-id="element-239">
                          Attach STL, OBJ, STEP, IGES, PDF, or image files of
                          your design or idea.
                        </p>
                        <label htmlFor="file-upload" className="flex flex-col items-center justify-center w-full py-8 px-4 bg-sandev-bg border-2 border-dashed border-sandev-border rounded-lg cursor-pointer hover:border-sandev-accent/50 transition-colors group" data-id="element-240">
                          <Upload size={28} className="text-sandev-muted group-hover:text-sandev-accent transition-colors mb-3" data-id="element-241" />
                          <span className="text-sm text-sandev-muted group-hover:text-sandev-text transition-colors" data-id="element-242">
                            <span className="text-sandev-accent font-medium" data-id="element-243">
                              Click to upload
                            </span>{' '}
                            or drag and drop
                          </span>
                          <span className="text-xs text-sandev-muted/60 mt-1" data-id="element-244">
                            STL, OBJ, STEP, IGES, PDF, PNG, JPG up to 50MB each
                          </span>
                          <input id="file-upload" type="file" multiple accept=".stl,.obj,.step,.stp,.iges,.igs,.pdf,.png,.jpg,.jpeg,.3mf" onChange={handleFileChange} className="hidden" data-id="element-245" />
                        </label>

                        {files.length > 0 && <div className="mt-4 space-y-2" data-id="element-246">
                            {files.map((file, index) => <div key={index} className="flex items-center justify-between bg-sandev-bg border border-sandev-border rounded-lg px-4 py-3" data-id="element-247">
                                <div className="flex items-center gap-3 min-w-0" data-id="element-248">
                                  <FileIcon size={18} className="text-sandev-accent shrink-0" data-id="element-249" />
                                  <div className="min-w-0" data-id="element-250">
                                    <p className="text-sm text-sandev-text truncate" data-id="element-251">
                                      {file.name}
                                    </p>
                                    <p className="text-xs text-sandev-muted" data-id="element-252">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                                <button type="button" onClick={() => removeFile(index)} className="text-sandev-muted hover:text-red-400 transition-colors shrink-0 ml-3" data-id="element-253">
                                  <X size={16} data-id="element-254" />
                                </button>
                              </div>)}
                          </div>}
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2" data-id="element-255">
                        <p className="text-xs text-sandev-muted" data-id="element-256">
                          <span className="text-sandev-accent" data-id="element-257">*</span> Required
                          fields
                        </p>
                        <button type="submit" disabled={sending} className="w-full sm:w-auto px-8 py-3.5 bg-sandev-accent hover:bg-sandev-accentHover disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all shadow-[0_0_15px_rgba(0,180,216,0.3)] hover:shadow-[0_0_25px_rgba(0,180,216,0.5)] disabled:shadow-none flex items-center justify-center gap-2" data-id="element-258">
                          {sending ? <>
                              <Loader2 size={18} className="animate-spin" data-id="element-259" />
                              Sending...
                            </> : <>
                              <Send size={18} data-id="element-260" />
                              Send Message
                            </>}
                        </button>
                      </div>
                    </form>
                  </>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}